import ky, { type Hooks as KyHooks, type Options as KyOptions, type KyInstance } from 'ky'

import { DEFAULT_CLIENT_RETRY } from '../constants'

import { HttpError } from './errors'

import type { HttpRequest, HttpResponse, TransportAdapter, RetryConfig, Hooks } from '../types'

function headersToRecord(headers: Headers): Record<string, string> {
  const record: Record<string, string> = {}
  headers.forEach((value, key) => {
    record[key] = value
  })
  return record
}

export interface KyTransportConfig {
  baseURL: string
  timeout?: number
  headers?: Record<string, string>
  retry?: Partial<RetryConfig> | false
  hooks?: Hooks
}

export class KyTransportAdapter implements TransportAdapter {
  private readonly instance: KyInstance

  constructor(config: KyTransportConfig) {
    const retryOpts = this.buildRetryOptions(config.retry)

    this.instance = ky.create({
      prefixUrl: config.baseURL,
      timeout: config.timeout,
      headers: config.headers,
      retry: retryOpts,
      ...(config.hooks && { hooks: config.hooks as KyHooks }),
    } as KyOptions)
  }

  private serializeSearchParamsRaw(searchParams: Record<string, string | number | boolean | undefined>): string {
    return Object.entries(searchParams)
      .filter(([, v]) => v != undefined)
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
      .join('&')
  }

  async execute<T>(request: HttpRequest): Promise<HttpResponse<T>> {
    const start = Date.now()

    let url = request.url.startsWith('/') ? request.url.slice(1) : request.url
    if (request.searchParams && Object.keys(request.searchParams).length > 0) {
      const queryString = this.serializeSearchParamsRaw(request.searchParams)
      url = `${url}${url.includes('?') ? '&' : '?'}${queryString}`
    }

    const options: KyOptions = {
      method: request.method,
      headers: request.headers,
      signal: request.signal,
      timeout: request.timeout,
      ...(request.retry !== undefined && { retry: this.buildRetryOptions(request.retry) }),
    }

    if (request.body !== undefined && request.method !== 'GET' && request.method !== 'HEAD') {
      const body = request.body
      if (body instanceof FormData || body instanceof Blob || ArrayBuffer.isView(body)) {
        options.body = body as BodyInit
      } else {
        options.json = body
      }
    }

    try {
      const response = await this.instance(url, options)
      const contentType = response.headers.get('content-type')
      let data: T
      if (contentType?.includes('application/json')) {
        data = (await response.json()) as T
      } else {
        data = (await response.text()) as T
      }

      return {
        data,
        status: response.status,
        statusText: response.statusText,
        headers: headersToRecord(response.headers),
        url: response.url,
        duration: Date.now() - start,
        fromCache: false,
        retryCount: 0,
      }
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        const kyErr = error as { response: Response; message: string }
        let body: unknown
        try {
          body = await kyErr.response.json()
        } catch {
          body = undefined
        }
        throw new HttpError({
          message: kyErr.message || `HTTP ${kyErr.response.status}`,
          status: kyErr.response.status,
          statusText: kyErr.response.statusText,
          url: request.url,
          method: request.method,
          responseBody: body,
        })
      }
      throw error
    }
  }

  private buildRetryOptions(retry?: Partial<RetryConfig> | false): KyOptions['retry'] {
    if (retry === false) return 0
    if (!retry) return undefined

    const merged = { ...DEFAULT_CLIENT_RETRY, ...retry }

    const delayMs = merged.delay
    const maxDelayMs = merged.maxDelay
    const backoffFactor = merged.backoffFactor
    const jitter = merged.jitter

    const opts: NonNullable<KyOptions['retry']> = {
      limit: merged.maxRetries,
      methods: merged.retryableMethods.map(m => m.toLowerCase()),
      statusCodes: merged.retryableStatusCodes,
      backoffLimit: maxDelayMs,
      delay: (attemptCount: number) => {
        const base = Math.min(delayMs * Math.pow(backoffFactor, attemptCount - 1), maxDelayMs)
        return base + Math.random() * jitter
      },
    }
    return opts as KyOptions['retry']
  }
}
